/**
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * const object = { 'a': 1, 'b': 2 }
 * const other = { 'c': 3, 'd': 4 }
 *
 * const values = memoize(values)
 * values(object)
 * // => [1, 2]
 *
 * values(other)
 * // => [3, 4]
 *
 * object.a = 2
 * values(object)
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b'])
 * values(object)
 * // => ['a', 'b']
 */

function memoize(func, resolver) {
	if (typeof func !== 'function' || (resolver != null && typeof resolver !== 'function')) {
		throw new TypeError('Expected a function');
	}
	const memoized = function (...args) {
		const key = resolver ? resolver.apply(this, args) : args[0];
		const { cache } = memoized;

		if (cache.has(key)) {
			return cache.get(key);
		}
		const result = func.apply(this, args);
		memoized.cache = cache.set(key, result) || cache;
		return result;
	};
	memoized.cache = new (memoize.Cache || Map)();
	return memoized;
}

memoize.Cache = Map;

export default memoize;
